import { Component, forwardRef, Input } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { LibCoreLayoutModule } from '../core-layout';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PhoneNumberInputModule } from '../phone-number-input';
import { PipesModule } from '../../pipes';
import { TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  BusinessObject,
  COMMUNICATION_WAY,
  Customer,
  DATE_FORMAT,
} from '../../models';
import { ControlValueAccessorComponent } from '../../core';
import { BreakpointService } from '../../services';
import { takeUntil } from 'rxjs/operators';
import { MatRadioModule } from '@angular/material/radio';
import { DateTimeUtils } from '../../utils';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'lib-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrl: './customer-form.component.scss',
  standalone: true,
  imports: [
    LibCoreLayoutModule,
    PhoneNumberInputModule,
    PipesModule,
    AsyncPipe,
    FlexModule,
    TranslateModule,
    ReactiveFormsModule,
    // material design
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatRadioModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerFormComponent),
      multi: true,
    },
  ],
})
export class CustomerFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public readonly configValue = {
    name: { maxLength: 50 },
    surname: { maxLength: 50 },
    note: { maxLength: 400 },
    email: { maxLength: 100 },
  };

  public readonly FORM_NAME = {
    POINT_UPDATE: 'pointUpdate',
    NAME: 'name',
    SURNAME: 'surname',
    PHONE_NR: 'phoneNr',
    EMAIL: 'email',
    BIRTHDAY: 'birthdayAsDate',
    NOTE: 'note',
    COMMUNICATION_WAY: 'communicationWay',
    NOTIFICATION_SUBSCRIBED: 'notificationSubscribed',
    BLACK_LISTED: 'blackListed',
  };

  public readonly COMMUNICATION_WAY = COMMUNICATION_WAY;

  @Input() public businessObject: BusinessObject;
  @Input() public displayFullContactData: boolean;
  @Input() public simplifiedFormForGuestRegistration: boolean;
  @Input() public isAbleToEditLoyalPoint: boolean;
  @Input() public isAbleToEditCommunicationWay: boolean;

  public originalCustomer: Customer;
  public submitForm: FormGroup;

  constructor(public breakpointService: BreakpointService) {
    super();

    this.submitForm = new FormGroup({
      [this.FORM_NAME.POINT_UPDATE]: new FormControl('', [
        Validators.pattern(/^-?\d+$/),
      ]),
      [this.FORM_NAME.NAME]: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.configValue.name.maxLength),
      ]),
      [this.FORM_NAME.SURNAME]: new FormControl('', [
        Validators.maxLength(this.configValue.surname.maxLength),
      ]),
      [this.FORM_NAME.PHONE_NR]: new FormControl('', [Validators.required]),
      [this.FORM_NAME.EMAIL]: new FormControl('', [
        Validators.email,
        Validators.maxLength(this.configValue.email.maxLength),
      ]),
      [this.FORM_NAME.BIRTHDAY]: new FormControl(null, []),
      [this.FORM_NAME.NOTE]: new FormControl('', [
        Validators.maxLength(this.configValue.note.maxLength),
      ]),
      [this.FORM_NAME.COMMUNICATION_WAY]: new FormControl(
        COMMUNICATION_WAY.SMS
      ),
      [this.FORM_NAME.NOTIFICATION_SUBSCRIBED]: new FormControl(true),
      [this.FORM_NAME.BLACK_LISTED]: new FormControl(false),
    });

    this.submitForm.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value) => {
        if (this.submitForm.invalid) {
          this._onChange(null);
        } else {
          this._onChange(this.generateDataToEmit(value));
        }
      });

    this.controlCommunicationWay.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value) => {
        this.controlEmail.setValidators([
          Validators.email,
          Validators.maxLength(this.configValue.email.maxLength),
        ]);

        if (value === COMMUNICATION_WAY.EMAIL) {
          this.controlEmail.addValidators(Validators.required);
        }

        this.controlEmail.updateValueAndValidity();
      });
  }

  public get controlName(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.NAME);
  }

  public get controlSurname(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.SURNAME);
  }

  public get controlPhoneNr(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.PHONE_NR);
  }

  public get controlEmail(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.EMAIL);
  }

  public get controlBirthday(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.BIRTHDAY);
  }

  public get controlNote(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.NOTE);
  }

  public get controlCommunicationWay(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.COMMUNICATION_WAY);
  }

  public get controlNotificationSubscribed(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.NOTIFICATION_SUBSCRIBED);
  }

  public get controlPointUpdate(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.POINT_UPDATE);
  }

  public get controlBlackListed(): AbstractControl {
    return this.submitForm.get(this.FORM_NAME.BLACK_LISTED);
  }

  private generateDataToEmit(formValue: Customer): Customer {
    const customerToEmit: Customer = {
      ...(this.originalCustomer || {}),
      ...formValue,
    };

    customerToEmit.email = customerToEmit.email.trim() || null;
    customerToEmit.birthday = customerToEmit.birthdayAsDate
      ? DateTimeUtils.formatDateToString(
          customerToEmit.birthdayAsDate,
          DATE_FORMAT.DATE_FORMAT_BACKEND
        )
      : null;

    return customerToEmit;
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(customer: Customer) {
    if (customer?.id && !this.originalCustomer?.id) {
      this.originalCustomer = { ...customer };
    }

    this.submitForm.patchValue(customer || {});
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.submitForm.disable();
    } else {
      this.submitForm.enable();
    }
  }
}
